<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import { getNode } from '@formkit/core'

const formkitProps = defineProps<{
  context: FormKitFrameworkContext
}>()

const props = computed(() => formkitProps.context?.attrs?.props || {})

type Hotspot = {
  id: number
  positionX: number
  positionY: number
  [key: string]: any
}

const watchedImageUrl = ref('')

const imageUrl = computed(() => {
  if (props.value.imageUrl) {
    return props.value.imageUrl
  }
  return watchedImageUrl.value
})

const getImageUrlFromValue = (value: any): string => {
  if (!value) return ''

  if (typeof value === 'string') {
    return value
  }

  if (value.file instanceof File) {
    return URL.createObjectURL(value.file)
  }

  if (value.file?.fileUrl) {
    return value.file.fileUrl
  }

  if (value.image?.fileUrl) {
    return value.image.fileUrl
  }

  if (value.blob) {
    return value.blob
  }

  if (value.thumb) {
    return value.thumb
  }

  return ''
}

const setupImageFieldWatcher = () => {
  const imageField = props.value.imageField
  if (!imageField) return

  const checkImageNode = () => {
    const imageNode = getNode(imageField)
    if (imageNode) {
      const updateImageUrl = () => {
        const url = getImageUrlFromValue(imageNode.value)
        if (url) {
          watchedImageUrl.value = url
        }
      }

      updateImageUrl()
      imageNode.on('commit', updateImageUrl)
      imageNode.on('input', updateImageUrl)
    } else {
      setTimeout(checkImageNode, 100)
    }
  }

  checkImageNode()
}

watch(
  () => props.value.imageUrl,
  (newUrl) => {
    if (newUrl) {
      watchedImageUrl.value = newUrl
    }
  },
  { immediate: true },
)

const hotspots = ref<Hotspot[]>([])
const selectedHotspotId = ref<number | null>(null)
const isDragging = ref(false)
const draggedHotspotId = ref<number | null>(null)
const imageContainerRef = ref<HTMLElement | null>(null)

const formNode = shallowRef<any>(null)
const overlayOpened = ref(false)
const editingHotspot = ref<Hotspot | null>(null)
const tempIdCounter = ref(-1)

const generateTempId = () => {
  return tempIdCounter.value--
}

onMounted(() => {
  setupImageFieldWatcher()

  const value = formkitProps.context._value
  if (Array.isArray(value) && value.length > 0) {
    hotspots.value = value.map((item) => ({
      ...item,
      // Keep real IDs, generate temp IDs for items without one
      id: item.id ? item.id : generateTempId(),
    }))
  }
})

const inputHotspots = () => {
  const items = hotspots.value.map(({ id, ...rest }) => ({
    ...rest,
    // Only include positive IDs (real server IDs), exclude temp negative IDs
    id: id && id > 0 ? id : undefined,
  }))
  formkitProps.context.node.input(items)
}

const getPositionFromEvent = (
  event: MouseEvent,
): { x: number; y: number } | null => {
  if (!imageContainerRef.value) return null

  const rect = imageContainerRef.value.getBoundingClientRect()
  const x = ((event.clientX - rect.left) / rect.width) * 100
  const y = ((event.clientY - rect.top) / rect.height) * 100

  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
  }
}

const onImageClick = (event: MouseEvent) => {
  if (isDragging.value) return

  const position = getPositionFromEvent(event)
  if (!position) return

  const newHotspot: Hotspot = {
    id: generateTempId(),
    positionX: Math.round(position.x * 100) / 100,
    positionY: Math.round(position.y * 100) / 100,
  }

  hotspots.value.push(newHotspot)
  selectedHotspotId.value = newHotspot.id
  inputHotspots()

  if (props.value.schema) {
    openEditOverlay(newHotspot)
  }
}

const onHotspotMouseDown = (event: MouseEvent, hotspot: Hotspot) => {
  event.stopPropagation()
  event.preventDefault()

  selectedHotspotId.value = hotspot.id
  draggedHotspotId.value = hotspot.id
  isDragging.value = true

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging.value || !draggedHotspotId.value) return

    const position = getPositionFromEvent(e)
    if (!position) return

    const index = hotspots.value.findIndex(
      (h) => h.id === draggedHotspotId.value,
    )
    if (index !== -1) {
      hotspots.value[index].positionX = Math.round(position.x * 100) / 100
      hotspots.value[index].positionY = Math.round(position.y * 100) / 100
    }
  }

  const onMouseUp = () => {
    if (isDragging.value) {
      inputHotspots()
    }
    isDragging.value = false
    draggedHotspotId.value = null
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

const onHotspotClick = (event: MouseEvent, hotspot: Hotspot) => {
  event.stopPropagation()
  selectedHotspotId.value = hotspot.id
}

const onHotspotDoubleClick = (event: MouseEvent, hotspot: Hotspot) => {
  event.stopPropagation()
  openEditOverlay(hotspot)
}

const openEditOverlay = (hotspot: Hotspot) => {
  editingHotspot.value = { ...hotspot }
  overlayOpened.value = true
}

const onSaveHotspot = () => {
  if (!editingHotspot.value) return

  const index = hotspots.value.findIndex(
    (h) => h.id === editingHotspot.value!.id,
  )
  if (index !== -1) {
    hotspots.value[index] = { ...editingHotspot.value }
  }

  inputHotspots()
  overlayOpened.value = false
  editingHotspot.value = null
}

const removeHotspot = (hotspotId: number) => {
  hotspots.value = hotspots.value.filter((h) => h.id !== hotspotId)
  if (selectedHotspotId.value === hotspotId) {
    selectedHotspotId.value = null
  }
  inputHotspots()
}

const selectHotspot = (hotspotId: number) => {
  selectedHotspotId.value = hotspotId
}
</script>

<template>
  <div class="image-hotspot-tagger">
    <div
      v-if="imageUrl"
      ref="imageContainerRef"
      class="relative bg-gray-100 rounded-lg overflow-hidden cursor-crosshair border border-gray-300"
      @click="onImageClick"
    >
      <img
        :src="imageUrl"
        class="block w-full h-auto select-none pointer-events-none"
        draggable="false"
      />

      <div
        v-for="(hotspot, index) in hotspots"
        :key="hotspot.id"
        class="absolute w-6 h-6 -ml-3 -mt-3 rounded-full flex items-center justify-center text-white text-xs font-bold cursor-move transition-transform hover:scale-110"
        :class="{
          'bg-primary-500 ring-2 ring-white shadow-lg':
            selectedHotspotId === hotspot.id,
          'bg-gray-700 ring-2 ring-white shadow':
            selectedHotspotId !== hotspot.id,
        }"
        :style="{
          left: `${hotspot.positionX}%`,
          top: `${hotspot.positionY}%`,
        }"
        @mousedown="onHotspotMouseDown($event, hotspot)"
        @click="onHotspotClick($event, hotspot)"
        @dblclick="onHotspotDoubleClick($event, hotspot)"
      >
        {{ index + 1 }}
      </div>
    </div>

    <div
      v-else
      class="bg-gray-100 rounded-lg p-8 text-center text-gray-500 border border-dashed border-gray-300"
    >
      No image selected. Please upload an image first.
    </div>

    <div v-if="hotspots.length > 0" class="mt-4 space-y-2">
      <div class="text-sm font-medium text-gray-700 mb-2">
        Hotspots ({{ hotspots.length }})
      </div>

      <div
        v-for="(hotspot, index) in hotspots"
        :key="hotspot.id"
        class="flex items-center gap-2 p-3 rounded-lg border transition-colors cursor-pointer"
        :class="{
          'border-primary-500 bg-primary-50': selectedHotspotId === hotspot.id,
          'border-gray-200 bg-white hover:bg-gray-50':
            selectedHotspotId !== hotspot.id,
        }"
        @click="selectHotspot(hotspot.id)"
      >
        <div
          class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          :class="{
            'bg-primary-500': selectedHotspotId === hotspot.id,
            'bg-gray-700': selectedHotspotId !== hotspot.id,
          }"
        >
          {{ index + 1 }}
        </div>

        <div class="flex-1 min-w-0">
          <div class="text-sm text-gray-600">
            Product:
            {{
              hotspot.product?.label
                ? JSON.parse(hotspot.product?.label)?.defaultText?.name
                : 'Not selected'
            }}
          </div>
        </div>

        <div class="flex items-center gap-1">
          <button
            v-if="props.schema"
            type="button"
            class="p-1.5 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors"
            title="Edit hotspot"
            @click.stop="openEditOverlay(hotspot)"
          >
            <div class="i-heroicons-outline-pencil w-4 h-4" />
          </button>
          <button
            type="button"
            class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Remove hotspot"
            @click.stop="removeHotspot(hotspot.id)"
          >
            <div class="i-heroicons-outline-trash w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="!hotspots.length && imageUrl"
      class="mt-4 text-sm text-gray-500 text-center py-4 border border-dashed border-gray-300 rounded-lg"
    >
      Click on the image to add hotspots
    </div>

    <Overlay v-model="overlayOpened" title="Edit Hotspot" @save="onSaveHotspot">
      <div v-if="editingHotspot" class="space-y-4">
        <FormKit
          v-if="props.schema"
          :id="`hotspot-${editingHotspot.id}`"
          v-model="editingHotspot"
          type="form"
          :actions="false"
          @node="formNode = $event"
        >
          <FormKitSchema :schema="props.schema" />
        </FormKit>
      </div>
    </Overlay>
  </div>
</template>

<style lang="postcss" scoped>
.image-hotspot-tagger {
  @apply flex flex-col;
}
</style>
